import { useEffect, useMemo, useState } from "react";
import { Button, Form, Header, Icon, Input, Label, Loader, Message, Modal, Segment } from "semantic-ui-react";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "../DataTable";
import GuestHouseDropdown from "../GuestHouseDropdown";
import ChargeItemModal from "./ChargeItemModal";
import useBillingCatalog from "../../../hooks/useBillingCatalog";
import { useAuth } from "../../../context/AuthContext";
import { hasCapability } from "../../../utils/capabilities";
import { formatRupees } from "../../../utils/money";
import { totalGstLabel, validateBillingSettings } from "./billingForms";

const columnHelper = createColumnHelper();
const fieldError = (message) => (message ? { content: message, pointing: "below" } : false);

/* Manage → Billing: one guest house at a time — its GST rates and bill
   header, the headings charges are grouped under, and the items it sells. */
export default function BillingSettingsTab({ guestHouses }) {
  const { currentUser } = useAuth();
  const canManageItems = hasCapability(currentUser, "catalog.manage");
  const canManageSettings = hasCapability(currentUser, "billing_settings.manage");
  const [ghId, setGhId] = useState(guestHouses[0]?.id ?? null);
  useEffect(() => {
    if (!guestHouses.some((g) => g.id === ghId)) setGhId(guestHouses[0]?.id ?? null);
  }, [guestHouses, ghId]);
  const catalog = useBillingCatalog(ghId);

  if (!guestHouses.length) return <Message info content="No guest houses to set up." />;

  return (
    <div>
      {guestHouses.length > 1 && (
        <div style={{ marginBottom: "1em" }}>
          <span style={{ marginRight: 8, fontWeight: 600 }}>Guest house</span>
          <GuestHouseDropdown
            value={ghId}
            onChange={setGhId}
            options={guestHouses.map((g) => ({ value: g.id, label: g.name }))}
          />
        </div>
      )}
      {catalog.error && (
        <Message negative>
          {catalog.error} <Button size="mini" basic onClick={catalog.reload}>Try again</Button>
        </Message>
      )}
      {catalog.loading && !catalog.settings ? (
        <Loader active inline="centered" content="Loading billing settings…" />
      ) : (
        catalog.settings && (
          <>
            <GstSettings
              key={`settings-${ghId}`}
              settings={catalog.settings}
              onSave={catalog.saveSettings}
              readOnly={!canManageSettings}
            />
            <Headings catalog={catalog} readOnly={!canManageItems} />
            <Items catalog={catalog} readOnly={!canManageItems} />
          </>
        )
      )}
    </div>
  );
}

function GstSettings({ settings, onSave, readOnly }) {
  const [form, setForm] = useState(settings);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = ["cgstPercent", "sgstPercent", "retentionDays", "legalName", "gstin", "billingAddress"]
    .some((k) => String(form[k] ?? "") !== String(settings[k] ?? ""));
  const set = (key) => (_, d) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: d.value }));
  };

  const save = async () => {
    const errs = validateBillingSettings(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      setForm(await onSave(form));
      setSaved(true);
    } catch (e) {
      const fe = e.fieldErrors || {};
      setErrors({
        cgstPercent: fe.cgst_percent?.[0], sgstPercent: fe.sgst_percent?.[0],
        retentionDays: fe.retention_days?.[0], gstin: fe.gstin?.[0],
        save: Object.keys(fe).length ? "" : e.message || "Could not save.",
      });
    } finally {
      setSaving(false);
    }
  };

  const total = totalGstLabel(form.cgstPercent, form.sgstPercent);
  return (
    <Segment>
      <Header as="h4">GST and bill header</Header>
      <Form onSubmit={save}>
        <Form.Group widths="equal">
          <Form.Input id="billing-cgst" label="CGST %" inputMode="decimal" readOnly={readOnly}
            value={form.cgstPercent} onChange={set("cgstPercent")} error={fieldError(errors.cgstPercent)} />
          <Form.Input id="billing-sgst" label="SGST %" inputMode="decimal" readOnly={readOnly}
            value={form.sgstPercent} onChange={set("sgstPercent")} error={fieldError(errors.sgstPercent)} />
          <Form.Input id="billing-retention" label="Retention on early checkout (days of rent)" type="number" min={0} max={30}
            readOnly={readOnly} value={form.retentionDays} onChange={set("retentionDays")}
            error={fieldError(errors.retentionDays)} />
        </Form.Group>
        {total && (
          <p style={{ marginTop: -6, color: "#555" }}>
            GST in total: <strong>{total}</strong> — added on items and rooms marked "Charge GST",
            and never on official bookings. A change applies to bills when they are next refreshed;
            finalized bills keep the rate they were issued at.
          </p>
        )}
        <Form.Group widths="equal">
          <Form.Input id="billing-legal-name" label="Name on the bill" placeholder="Leave blank to use the guest house name"
            readOnly={readOnly} value={form.legalName} onChange={set("legalName")} />
          <Form.Input id="billing-gstin" label="GSTIN" placeholder="15 characters" maxLength={15} readOnly={readOnly}
            value={form.gstin} onChange={set("gstin")} error={fieldError(errors.gstin)} />
        </Form.Group>
        <Form.TextArea id="billing-address" label="Address on the bill" rows={2} placeholder="Leave blank to use the guest house address"
          readOnly={readOnly} value={form.billingAddress} onChange={set("billingAddress")} />
        {!readOnly && (
          <Button primary type="submit" loading={saving} disabled={!dirty || saving}>Save</Button>
        )}
        {saved && !dirty && <Label basic color="green"><Icon name="check" />Saved</Label>}
      </Form>
      {errors.save && <Message negative content={errors.save} />}
    </Segment>
  );
}

function Headings({ catalog, readOnly }) {
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // { id, name }
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      return true;
    } catch (e) {
      setError(e.fieldErrors?.name?.[0] || e.message || "Could not save.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Segment>
      <Header as="h4">
        Bill headings
        <Header.Subheader>Charges are totalled under these on the bill — e.g. Boarding, Laundry. Room rent always goes under "Rent".</Header.Subheader>
      </Header>
      {catalog.categories.length === 0 && <p>No headings yet. Add one before adding items.</p>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        {catalog.categories.map((c) =>
          editing?.id === c.id ? (
            <Input key={c.id} size="small" autoFocus value={editing.name} disabled={busy}
              onChange={(_, d) => setEditing({ ...editing, name: d.value })}
              onKeyDown={async (ev) => {
                if (ev.key === "Escape") setEditing(null);
                if (ev.key === "Enter" && editing.name.trim()
                    && await run(() => catalog.renameCategory(c.id, editing.name))) setEditing(null);
              }}
              action={{ icon: "check", "aria-label": "Save heading", onClick: async () => {
                if (editing.name.trim() && await run(() => catalog.renameCategory(c.id, editing.name))) setEditing(null);
              } }}
            />
          ) : (
            <Label key={c.id} size="large">
              {c.name}
              {!readOnly && (
                <>
                  <Icon name="pencil" link aria-label={`Rename ${c.name}`} style={{ marginLeft: 8 }}
                    onClick={() => setEditing({ id: c.id, name: c.name })} />
                  <Icon name="delete" link aria-label={`Delete ${c.name}`}
                    onClick={() => run(() => catalog.deleteCategory(c.id))} />
                </>
              )}
            </Label>
          ),
        )}
      </div>
      {!readOnly && (
        <Form onSubmit={async () => {
          if (newName.trim() && await run(() => catalog.addCategory(newName))) setNewName("");
        }}>
          <Form.Group inline>
            <Form.Input placeholder="New heading, e.g. Boarding" value={newName} disabled={busy}
              aria-label="New heading name" onChange={(_, d) => setNewName(d.value)} />
            <Button type="submit" disabled={!newName.trim() || busy}><Icon name="plus" />Add heading</Button>
          </Form.Group>
        </Form>
      )}
      {error && <Message negative size="small" content={error} />}
    </Segment>
  );
}

function Items({ catalog, readOnly }) {
  const [modal, setModal] = useState(null); // { item } — item null = add
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState("");

  const act = async (fn) => {
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message || "Could not save.");
    }
  };

  const columns = useMemo(() => [
    columnHelper.accessor("name", { header: "Item" }),
    columnHelper.accessor("categoryName", { header: "Heading" }),
    columnHelper.accessor("unitPrice", {
      header: "Price before GST",
      cell: (info) => `${formatRupees(info.getValue())}${info.row.original.unitLabel ? ` / ${info.row.original.unitLabel}` : ""}`,
    }),
    columnHelper.accessor("gstApplicable", { header: "GST", cell: (i) => (i.getValue() ? "Charged" : "Not charged") }),
    columnHelper.accessor("inDailyGrid", { header: "Daily grid", cell: (i) => (i.getValue() ? "Yes" : "No") }),
    columnHelper.accessor("isActive", {
      header: "Status",
      cell: (i) => (i.getValue() ? <Label color="green" basic size="small">In use</Label>
        : <Label basic size="small">Switched off</Label>),
    }),
    ...(readOnly ? [] : [columnHelper.display({
      id: "actions", header: "Actions", enableSorting: false,
      cell: (info) => {
        const item = info.row.original;
        return (
          <Button.Group size="small">
            <Button basic onClick={() => setModal({ item })}><Icon name="edit" />Edit</Button>
            <Button basic onClick={() => act(() => catalog.setItemActive(item.id, !item.isActive))}>
              {item.isActive ? "Switch off" : "Switch on"}
            </Button>
            <Button basic negative aria-label={`Delete ${item.name}`} onClick={() => setConfirmDelete(item)}>
              <Icon name="trash" />
            </Button>
          </Button.Group>
        );
      },
    })]),
  ], [catalog, readOnly]);

  return (
    <Segment>
      <Header as="h4">
        Items
        <Header.Subheader>What the guest house charges for. Bills keep the price an item had when it was entered, so changing a price here never alters an existing bill.</Header.Subheader>
      </Header>
      {!readOnly && (
        <Button primary size="small" disabled={!catalog.categories.length} onClick={() => setModal({ item: null })}
          style={{ marginBottom: 10 }}>
          <Icon name="plus" />Add item
        </Button>
      )}
      {error && <Message negative size="small" content={error} />}
      <DataTable data={catalog.items} columns={columns} emptyMessage="No items yet." />
      {modal && (
        <ChargeItemModal item={modal.item} categories={catalog.categories}
          onSave={catalog.saveItem} onClose={() => setModal(null)} />
      )}
      {confirmDelete && (
        <Modal open size="tiny" onClose={() => setConfirmDelete(null)}>
          <Modal.Header>Delete {confirmDelete.name}?</Modal.Header>
          <Modal.Content>
            Bills that already include it keep their lines. To stop using it but keep it on the list, switch it off instead.
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button negative onClick={async () => {
              const item = confirmDelete;
              setConfirmDelete(null);
              await act(() => catalog.deleteItem(item.id));
            }}>Delete</Button>
          </Modal.Actions>
        </Modal>
      )}
    </Segment>
  );
}
